import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, MessageSquare, Mail, Handshake, Plus, Eye } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    {
      title: 'Blog Posts',
      value: stats.posts.total,
      subValue: `${stats.posts.published} published`,
      icon: FileText,
      color: '#2D3E55',
      href: '/admin/blog',
    },
    {
      title: 'Comments',
      value: stats.comments.total,
      subValue: `${stats.comments.pending} pending`,
      icon: MessageSquare,
      color: '#F15A24',
      href: '/admin/comments',
      badge: stats.comments.pending > 0 ? stats.comments.pending : null,
    },
    {
      title: 'Contact Messages',
      value: stats.contacts.total,
      subValue: `${stats.contacts.unread} unread`,
      icon: Mail,
      color: '#F7B500',
      href: '/admin/contacts',
      badge: stats.contacts.unread > 0 ? stats.contacts.unread : null,
    },
    {
      title: 'Partnership Inquiries',
      value: stats.partnerships.total,
      subValue: `${stats.partnerships.unread} unread`,
      icon: Handshake,
      color: '#6EC1E4',
      href: '/admin/partnerships',
      badge: stats.partnerships.unread > 0 ? stats.partnerships.unread : null,
    },
  ] : [];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#2D3E55]">
              Dashboard
            </h1>
            <p className="font-body text-slate-600 mt-1">
              Welcome to His Presence Chapel Admin
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/blog/new">
              <Button className="bg-[#F15A24] hover:bg-[#d64d1e] text-white" data-testid="new-post-btn">
                <Plus size={18} className="mr-2" />
                New Post
              </Button>
            </Link>
            <Link to="/" target="_blank">
              <Button variant="outline" data-testid="view-site-btn">
                <Eye size={18} className="mr-2" />
                View Site
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-12 w-12 bg-slate-200 rounded-lg mb-4"></div>
                  <div className="h-6 bg-slate-200 rounded w-1/2 mb-2"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={stat.href}>
                  <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer relative" data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
                    {stat.badge && (
                      <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                        {stat.badge}
                      </span>
                    )}
                    <CardContent className="p-6">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                        style={{ backgroundColor: `${stat.color}15` }}
                      >
                        <stat.icon size={24} style={{ color: stat.color }} />
                      </div>
                      <h3 className="font-heading text-3xl font-bold text-[#2D3E55]">
                        {stat.value}
                      </h3>
                      <p className="font-body text-slate-600 text-sm mt-1">
                        {stat.title}
                      </p>
                      <p className="font-body text-slate-400 text-xs mt-1">
                        {stat.subValue}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <Card className="mt-8" data-testid="quick-actions">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-[#2D3E55]">
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/admin/blog/new">
                <Button variant="outline" className="w-full justify-start gap-2">
                  <FileText size={18} />
                  Create Blog Post
                </Button>
              </Link>
              <Link to="/admin/comments?pending=true">
                <Button variant="outline" className="w-full justify-start gap-2">
                  <MessageSquare size={18} />
                  Review Comments
                </Button>
              </Link>
              <a href={`${API_URL}/api/admin/export/contacts`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full justify-start gap-2">
                  <Mail size={18} />
                  Export Contacts CSV
                </Button>
              </a>
              <a href={`${API_URL}/api/admin/export/partnerships`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full justify-start gap-2">
                  <Handshake size={18} />
                  Export Partnerships CSV
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
